import { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import api, { BASE } from "../api";

// Which thread is open is per-browser UI state, not data — persisted so a
// page reload re-opens the same conversation instead of the widget looking
// like it lost the message (the message itself always stays server-side).
const ACTIVE_KEY = 'hrms_chat_active_user';
const readStoredActive = () => { try { const v = parseInt(localStorage.getItem(ACTIVE_KEY), 10); return v || null; } catch { return null; } };
const writeStoredActive = (userId) => { try { userId ? localStorage.setItem(ACTIVE_KEY, String(userId)) : localStorage.removeItem(ACTIVE_KEY); } catch { /* ignore */ } };

// Soft two-note "bell" chime for an incoming message, synthesized with the
// Web Audio API — no sound asset to ship or license. Each note layers a
// fundamental with a quiet octave overtone (a sine alone reads as a harsh
// electronic beep; the overtone plus a shaped attack/decay gives it a
// rounder, more instrument-like timbre). One AudioContext is reused across
// the session rather than created per-message.
let chimeCtx = null;
// Every oscillator the ring has scheduled. Tones are queued up to ~1s ahead
// of the audio clock, so clearing the interval alone leaves those already
// queued and they keep sounding after accept/decline/mute — stopping them
// directly is what makes the ring cut off immediately.
const ringNodes = [];
function playTone(ctx, freq, start, duration, peakGain) {
  const now = ctx.currentTime + start;
  const osc = ctx.createOscillator();
  const overtone = ctx.createOscillator();
  const oscGain = ctx.createGain();
  const overtoneGain = ctx.createGain();
  osc.type = 'sine';
  overtone.type = 'sine';
  osc.frequency.value = freq;
  overtone.frequency.value = freq * 2;
  // Fast attack, slow exponential decay — a pluck/bell envelope rather
  // than a flat-topped electronic beep.
  oscGain.gain.setValueAtTime(0, now);
  oscGain.gain.linearRampToValueAtTime(peakGain, now + 0.012);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  overtoneGain.gain.setValueAtTime(0, now);
  overtoneGain.gain.linearRampToValueAtTime(peakGain * 0.18, now + 0.012);
  overtoneGain.gain.exponentialRampToValueAtTime(0.0001, now + duration * 0.6);
  osc.connect(oscGain).connect(ctx.destination);
  overtone.connect(overtoneGain).connect(ctx.destination);
  osc.start(now); osc.stop(now + duration);
  overtone.start(now); overtone.stop(now + duration);
  ringNodes.push(osc, overtone);
}
function playIncomingChime() {
  try {
    if (!chimeCtx) chimeCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (chimeCtx.state === 'suspended') chimeCtx.resume();
    // A rising perfect fourth (D5 -> G5) — reads as a clean, professional
    // "received" cue rather than a toy-like jingle. Gain doubled from the
    // original 0.16 — that read as too quiet to notice in practice.
    playTone(chimeCtx, 587.33, 0, 0.5, 0.45);
    playTone(chimeCtx, 783.99, 0.09, 0.55, 0.45);
  } catch { /* autoplay blocked or unsupported — silent, non-critical */ }
}

// A single soft, quiet pop — fires on MY screen when someone I sent a
// message to while they were offline just came online and received it
// (tick flips single -> double-grey). Deliberately quieter and shorter
// than playIncomingChime so "they got it" doesn't compete with "new
// message arrived" — it's a confirmation, not an alert.
function playDeliveredChime() {
  try {
    if (!chimeCtx) chimeCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (chimeCtx.state === 'suspended') chimeCtx.resume();
    playTone(chimeCtx, 523.25, 0, 0.22, 0.2);
  } catch { /* autoplay blocked or unsupported — silent, non-critical */ }
}

// Sharper, more insistent triple-ping for an @mention — deliberately
// distinct from the plain-message chime above (higher, faster, three notes
// instead of two) so being mentioned stands out from ordinary chat noise,
// whether or not that group's thread is currently open.
function playMentionAlert() {
  try {
    if (!chimeCtx) chimeCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (chimeCtx.state === 'suspended') chimeCtx.resume();
    playTone(chimeCtx, 880, 0, 0.22, 0.5);
    playTone(chimeCtx, 1046.5, 0.12, 0.22, 0.5);
    playTone(chimeCtx, 1318.5, 0.24, 0.3, 0.5);
  } catch { /* autoplay blocked or unsupported — silent, non-critical */ }
}

// A short ascending major arpeggio (C5-E5-G5-C6) for an incoming call,
// looped every ~1.8s — reads as a bright, modern "ringtone" chime rather
// than the harsh two-tone "brring-brring" this replaced. Louder than the
// message chime (0.42 vs 0.32 peak gain) since a call is more urgent and
// needs to cut through. Module-level singleton (one ring at a time) so
// Mute/answer/decline from anywhere can stop it reliably.
let ringCtx = null;
let ringIntervalId = null;
function playRingCycle() {
  try {
    if (!ringCtx) ringCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (ringCtx.state === 'suspended') ringCtx.resume();
    playTone(ringCtx, 523.25, 0, 0.3, 0.55);    // C5
    playTone(ringCtx, 659.25, 0.16, 0.3, 0.55); // E5
    playTone(ringCtx, 783.99, 0.32, 0.3, 0.55); // G5
    playTone(ringCtx, 1046.5, 0.48, 0.45, 0.55); // C6 — resolves the phrase
  } catch { /* autoplay blocked or unsupported — silent, non-critical */ }
}
function startRingtone() {
  stopRingtone();
  playRingCycle();
  ringIntervalId = setInterval(playRingCycle, 1800);
}
function stopRingtone() {
  if (ringIntervalId) { clearInterval(ringIntervalId); ringIntervalId = null; }
  while (ringNodes.length) {
    try { ringNodes.pop().stop(); } catch { /* already stopped */ }
  }
}

// Owns the socket connection + all chat state: the 1:1 DM side (people
// directory, conversations, active thread) and the group/channel side
// (my groups, active group thread, members, mentions). Self-contained —
// reads the token the same way src/api.js does, so no prop-drilling from
// App.js beyond mounting <ChatWidget/> once.
export function useChat(enabled, myUserId) {
  const [people, setPeople] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingFrom, setTypingFrom] = useState({});

  const [groups, setGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupTypingUsers, setGroupTypingUsers] = useState([]);
  // Pinned messages for whichever DM or group is currently open — shared
  // with the whole conversation, refreshed on open and on 'message:pin'.
  const [pinnedMessages, setPinnedMessages] = useState([]);

  // { fromUserId, meetLink, toUserId?, groupId? } | null — only the ids are
  // stored; the banner resolves a display name from `people`/`groupMembers`
  // at render time so it's never working off a stale closure.
  const [incomingCall, setIncomingCall] = useState(null);
  const incomingCallTimeoutRef = useRef(null);
  const incomingCallRef = useRef(null);
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);

  // { meetLink, targetName, toUserId?, groupId? } | null — the caller's own
  // "Calling…" banner, shown the moment they start a call (there was
  // previously no feedback at all on the caller's side beyond the chat
  // message and the new tab). toUserId/groupId identify who to notify if
  // the caller hangs up before it's answered.
  const [outgoingCall, setOutgoingCall] = useState(null);
  const outgoingCallTimeoutRef = useRef(null);
  const outgoingCallRef = useRef(null);
  useEffect(() => { outgoingCallRef.current = outgoingCall; }, [outgoingCall]);

  // Reply-to-a-specific-message threads. A "thread" is just a message
  // (DM or group) with replies hanging off it via parent_message_id —
  // `threads` only lists roots that have at least one reply.
  const [threads, setThreads] = useState([]);
  const [activeThreadRootId, setActiveThreadRootId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);

  const socketRef = useRef(null);
  const activeUserIdRef = useRef(null);
  // Whether the chat panel is actually on screen. activeUserId persists
  // across closes (it's restored from localStorage), so "this thread is the
  // active one" alone doesn't mean anyone is looking at it.
  const panelOpenRef = useRef(false);
  const activeGroupIdRef = useRef(null);
  const activeThreadRootIdRef = useRef(null);
  useEffect(() => { activeUserIdRef.current = activeUserId; }, [activeUserId]);
  useEffect(() => { activeGroupIdRef.current = activeGroupId; }, [activeGroupId]);
  useEffect(() => { activeThreadRootIdRef.current = activeThreadRootId; }, [activeThreadRootId]);

  const unreadTotal = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)
    + groups.reduce((sum, g) => sum + (g.unread_count || 0), 0);

  const loadConversations = useCallback(() => {
    api.get('/api/chat/conversations').then(d => { if (d) setConversations(d.data || []); }).catch(() => {});
  }, []);

  const loadGroups = useCallback(() => {
    api.get('/api/chat-groups/mine').then(d => { if (d) setGroups(d.data || []); }).catch(() => {});
  }, []);

  const loadChannels = useCallback(() => {
    api.get('/api/chat-groups/channels').then(d => { if (d) setChannels(d.data || []); }).catch(() => {});
  }, []);

  const loadThreads = useCallback(() => {
    api.get('/api/threads/mine').then(d => { if (d) setThreads(d.data || []); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const token = api.getToken();
    if (!token) return;

    api.get('/api/chat/people').then(d => { if (d) setPeople(d.data || []); }).catch(() => {});
    loadConversations();
    loadGroups();
    loadThreads();

    const socket = io(BASE, { auth: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('message:new', (msg) => {
      // Only chime for messages someone else sent us — not our own message
      // echoed back (sent from this tab, or synced from another tab/device).
      if (msg.sender_id !== myUserId) playIncomingChime();
      const belongsToOpenThread = panelOpenRef.current && (msg.sender_id === activeUserIdRef.current || msg.receiver_id === activeUserIdRef.current);
      if (belongsToOpenThread) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          // Own message echoed back to us (sent from another tab, or this
          // tab's own optimistic entry, identified by the clientId the
          // sender attached) — replace rather than duplicate.
          if (msg.clientId && prev.some(m => m.clientId === msg.clientId)) {
            return prev.map(m => {
              if (m.clientId !== msg.clientId) return m;
              if (m._previewUrl) URL.revokeObjectURL(m._previewUrl);
              return msg;
            });
          }
          return [...prev, msg];
        });
        // The thread this just landed in is already open/visible — mark it
        // read immediately instead of leaving it to the next openThread()
        // call, which only fires when a conversation is first switched
        // into. Without this, a message arriving while already viewing
        // that conversation stayed "unread" forever as far as the server
        // (and therefore the next conversations refetch) was concerned.
        if (msg.sender_id !== myUserId) {
          socketRef.current?.emit('message:read', { fromUserId: msg.sender_id });
        }
      }
      loadConversations();
    });

    // byUserId is the peer who just received my pending message(s) — flip
    // their tick to delivered wherever that thread is currently rendered,
    // and chime so I notice even if I'm not looking at that chat. The
    // server only fires this when it actually just delivered something, so
    // the chime here needs no extra "did anything change" guard.
    socket.on('message:status', ({ byUserId } = {}) => {
      if (!byUserId) return;
      setMessages(prev => prev.map(m => (m.receiver_id === byUserId && !m.delivered_at)
        ? { ...m, delivered_at: new Date().toISOString() } : m));
      playDeliveredChime();
      loadConversations();
    });

    socket.on('message:read', ({ byUserId }) => {
      setMessages(prev => prev.map(m => (m.receiver_id === byUserId && !m.read_at) ? { ...m, read_at: new Date().toISOString() } : m));
    });

    socket.on('typing', ({ fromUserId, isTyping }) => {
      setTypingFrom(prev => ({ ...prev, [fromUserId]: isTyping }));
    });

    // A reaction can land on a DM message, a group message, or a thread
    // root/reply — all three lists are just filtered views over the same
    // chat_messages rows, so update whichever one(s) actually hold it.
    socket.on('message:reaction', ({ messageId, reactions }) => {
      const apply = (list) => list.map(m => (m.id === messageId || String(m.id) === String(messageId)) ? { ...m, reactions } : m);
      setMessages(apply);
      setGroupMessages(apply);
      setThreadMessages(apply);
    });

    // The sender edited a message (within 10 minutes of sending) — update its text everywhere it shows.
    socket.on('message:edited', ({ messageId, body, edited_at }) => {
      const apply = (list) => list.map(m => (m.id === messageId || String(m.id) === String(messageId)) ? { ...m, body, edited_at } : m);
      setMessages(apply);
      setGroupMessages(apply);
      setThreadMessages(apply);
    });

    // The sender deleted a message for everyone — blank it in whichever list holds it.
    socket.on('message:deleted', ({ messageId }) => {
      const apply = (list) => list.map(m => (m.id === messageId || String(m.id) === String(messageId))
        ? { ...m, deleted_at: new Date().toISOString(), body: '', attachment_kind: null, attachment_path: null, attachment_name: null, reactions: [], pinned: false }
        : m);
      setMessages(apply);
      setGroupMessages(apply);
      setThreadMessages(apply);
      setPinnedMessages(list => list.filter(m => String(m.id) !== String(messageId)));
    });

    // A pin is shared with the whole conversation (not a personal
    // bookmark), so anyone else pinning/unpinning shows up live too. The
    // payload only carries {messageId, pinned} — cheap enough to just
    // re-fetch the pinned list for whichever conversation is open rather
    // than reconstruct the full pinned-message row (sender name, etc.)
    // from scratch here.
    socket.on('message:pin', ({ messageId, pinned }) => {
      const apply = (list) => list.map(m => (m.id === messageId || String(m.id) === String(messageId)) ? { ...m, pinned } : m);
      setMessages(apply);
      setGroupMessages(apply);
      setThreadMessages(apply);
      if (activeUserIdRef.current) {
        api.get(`/api/chat/messages/${activeUserIdRef.current}/pinned`).then(d => { if (d) setPinnedMessages(d.data || []); }).catch(() => {});
      } else if (activeGroupIdRef.current) {
        api.get(`/api/chat-groups/${activeGroupIdRef.current}/pinned-messages`).then(d => { if (d) setPinnedMessages(d.data || []); }).catch(() => {});
      }
    });

    // ── Groups/channels ────────────────────────────────────────────────
    socket.on('group:message:new', (msg) => {
      // Membership lines ("X added Y") are silent — no chime.
      if (msg.sender_id !== myUserId && !msg.system_event) {
        // A mention gets its own distinct, more insistent sound — always,
        // not just while that thread is closed, so being @mentioned never
        // blends into the general "someone posted" chime.
        if (msg.mentioned_user_ids?.includes(myUserId)) playMentionAlert();
        else playIncomingChime();
      }
      if (msg.group_id === activeGroupIdRef.current) {
        // Same as the DM case: a message landing in a group that's open on
        // screen is read straight away, not left counting as unread.
        if (panelOpenRef.current && msg.sender_id !== myUserId) {
          api.post(`/api/chat-groups/${msg.group_id}/read`, {}).catch(() => {});
        }
        setGroupMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          if (msg.clientId && prev.some(m => m.clientId === msg.clientId)) {
            return prev.map(m => {
              if (m.clientId !== msg.clientId) return m;
              if (m._previewUrl) URL.revokeObjectURL(m._previewUrl);
              return msg;
            });
          }
          return [...prev, msg];
        });
      }
      loadGroups();
    });
    socket.on('group:message:notify', () => loadGroups());
    socket.on('group:created', () => loadGroups());
    socket.on('group:typing', ({ groupId, fromUserId, isTyping }) => {
      if (groupId !== activeGroupIdRef.current || fromUserId === myUserId) return;
      setGroupTypingUsers(prev => isTyping ? [...new Set([...prev, fromUserId])] : prev.filter(id => id !== fromUserId));
    });
    socket.on('group:updated', (group) => {
      setGroups(prev => prev.map(g => g.id === group.id ? { ...g, ...group } : g));
    });
    socket.on('group:deleted', ({ groupId }) => {
      setGroups(prev => prev.filter(g => g.id !== groupId));
      if (activeGroupIdRef.current === groupId) {
        setActiveGroupId(null);
        setGroupMessages([]);
        setGroupMembers([]);
      }
    });
    socket.on('group:member-updated', ({ groupId, userId, role }) => {
      if (groupId === activeGroupIdRef.current) {
        setGroupMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m));
      }
    });
    socket.on('group:member-removed', ({ groupId, userId }) => {
      if (groupId === activeGroupIdRef.current) {
        setGroupMembers(prev => prev.filter(m => m.user_id !== userId));
      }
      loadGroups(); // member_count shown in the list needs refreshing too
    });

    // ── Thread replies ──────────────────────────────────────────────────
    socket.on('thread:reply:new', (msg) => {
      if (msg.sender_id !== myUserId) {
        if (msg.mentioned_user_ids?.includes(myUserId)) playMentionAlert();
        else playIncomingChime();
      }
      if (msg.parent_message_id === activeThreadRootIdRef.current) {
        setThreadMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          if (msg.clientId && prev.some(m => m.clientId === msg.clientId)) {
            return prev.map(m => {
              if (m.clientId !== msg.clientId) return m;
              if (m._previewUrl) URL.revokeObjectURL(m._previewUrl);
              return msg;
            });
          }
          return [...prev, msg];
        });
      }
      loadThreads();
    });

    // ── Incoming call ring — rings for 20s, then auto-dismisses like a
    // missed call if nobody answers/declines/mutes it first.
    socket.on('call:incoming', (payload) => {
      setIncomingCall(payload);
      clearTimeout(incomingCallTimeoutRef.current);
      startRingtone();
      incomingCallTimeoutRef.current = setTimeout(() => { stopRingtone(); setIncomingCall(null); }, 20000);
    });

    // Another tab of this same account answered or declined this call —
    // stop ringing here too and drop the banner, since it's already handled.
    socket.on('call:resolved', (key = {}) => {
      const cur = incomingCallRef.current;
      if (!cur || cur.fromUserId !== key.fromUserId || cur.groupId !== key.groupId) return;
      clearTimeout(incomingCallTimeoutRef.current);
      stopRingtone();
      setIncomingCall(null);
    });

    // Another tab of this account muted the ring — silence this one too,
    // but leave the banner up since the call itself is still open.
    socket.on('call:muted', (key = {}) => {
      const cur = incomingCallRef.current;
      if (!cur || cur.fromUserId !== key.fromUserId || cur.groupId !== key.groupId) return;
      stopRingtone();
    });

    // The caller hung up before this side answered — stop ringing now
    // instead of riding out the full 20s timeout. Matched on who's calling
    // (and which group, for a group call) so an unrelated call:cancelled
    // from someone else can't dismiss a different, still-live ring.
    socket.on('call:cancelled', ({ fromUserId, groupId } = {}) => {
      const cur = incomingCallRef.current;
      if (!cur || cur.fromUserId !== fromUserId || cur.groupId !== groupId) return;
      clearTimeout(incomingCallTimeoutRef.current);
      stopRingtone();
      setIncomingCall(null);
    });

    // The callee declined — stop the caller's ring-back immediately instead
    // of leaving the "Calling…" banner up for the full 20s.
    socket.on('call:declined', ({ fromUserId, groupId } = {}) => {
      const cur = outgoingCallRef.current;
      if (!cur || cur.toUserId !== fromUserId || cur.groupId !== groupId) return;
      clearTimeout(outgoingCallTimeoutRef.current);
      stopRingtone();
      setOutgoingCall(null);
    });

    // The callee actually joined — same as above, just the happy path.
    socket.on('call:answered', ({ fromUserId, groupId } = {}) => {
      const cur = outgoingCallRef.current;
      if (!cur || cur.toUserId !== fromUserId || cur.groupId !== groupId) return;
      clearTimeout(outgoingCallTimeoutRef.current);
      stopRingtone();
      setOutgoingCall(null);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      clearTimeout(incomingCallTimeoutRef.current);
      clearTimeout(outgoingCallTimeoutRef.current);
      stopRingtone();
    };
  }, [enabled, loadConversations, loadGroups, loadThreads, myUserId]);

  const openThread = useCallback((userId) => {
    setActiveGroupId(null);
    setGroupMessages([]);
    setActiveUserId(userId);
    writeStoredActive(userId);
    setMessages([]);
    setPinnedMessages([]);
    api.get('/api/chat/messages/' + userId).then(d => {
      if (d) setMessages((d.data || []).slice().reverse());
    }).catch(() => {});
    api.get(`/api/chat/messages/${userId}/pinned`).then(d => { if (d) setPinnedMessages(d.data || []); }).catch(() => {});
    socketRef.current?.emit('message:read', { fromUserId: userId });
    setConversations(prev => prev.map(c => c.user_id === userId ? { ...c, unread_count: 0 } : c));
  }, []);

  const closeThread = useCallback(() => {
    setActiveUserId(null);
    writeStoredActive(null);
    setMessages([]);
    setPinnedMessages([]);
  }, []);

  // Restore whichever thread was open before a reload, once per mount.
  useEffect(() => {
    if (!enabled) return;
    const stored = readStoredActive();
    if (stored) openThread(stored);
  }, [enabled]); // eslint-disable-line

  // `replyTo`, when given, is a small preview object the caller builds —
  // { id, body, attachment_kind, attachment_name, sender_name } — for a
  // WhatsApp-style inline quote reply (same chat, not the separate Threads
  // tab feature). Built by the caller rather than guessed here because a
  // plain DM message row has no sender_name of its own; the component
  // already knows whether it was "You" or the other person.
  const sendMessage = useCallback((toUserId, body, replyTo = null) => {
    const text = body.trim();
    if (!text || !toUserId) return;
    const clientId = 'c' + Date.now() + Math.random().toString(36).slice(2);
    const optimistic = {
      id: clientId, clientId, sender_id: myUserId, receiver_id: toUserId,
      body: text, created_at: new Date().toISOString(), delivered_at: null, read_at: null, _optimistic: true,
      ...(replyTo ? {
        reply_to_message_id: replyTo.id,
        reply_to_body: replyTo.body,
        reply_to_attachment_kind: replyTo.attachment_kind,
        reply_to_attachment_name: replyTo.attachment_name,
        reply_to_sender_name: replyTo.sender_name,
      } : {}),
    };
    setMessages(prev => [...prev, optimistic]);
    socketRef.current?.emit('message:send', { toUserId, body: text, clientId, replyToMessageId: replyTo?.id }, (res) => {
      if (res?.ok) {
        setMessages(prev => prev.map(m => m.clientId === clientId ? res.message : m));
      }
    });
  }, [myUserId]);

  const setTyping = useCallback((toUserId, isTyping) => {
    if (!toUserId) return;
    socketRef.current?.emit(isTyping ? 'typing:start' : 'typing:stop', { toUserId });
  }, []);

  // One endpoint handles DM, group, and thread messages alike (they're all
  // just chat_messages rows) — no optimistic update needed here, the
  // server broadcasts the updated reaction summary back over the socket
  // (including to the reactor) and the 'message:reaction' listener above
  // applies it to whichever list actually holds this message.
  // Called by ChatWidget whenever the panel opens/closes. On open, the
  // active DM is marked read — that's what makes anything that arrived while
  // the panel was closed stop counting as unread once someone looks at it.
  const setPanelOpen = useCallback((open) => {
    panelOpenRef.current = open;
    if (open && activeUserIdRef.current) {
      socketRef.current?.emit('message:read', { fromUserId: activeUserIdRef.current });
      setConversations(prev => prev.map(c => c.user_id === activeUserIdRef.current ? { ...c, unread_count: 0 } : c));
    }
    if (open && activeGroupIdRef.current) {
      const gid = activeGroupIdRef.current;
      api.post(`/api/chat-groups/${gid}/read`, {}).catch(() => {});
      setGroups(prev => prev.map(g => g.id === gid ? { ...g, unread_count: 0 } : g));
    }
  }, []);

  // Edit my own text message (server enforces the 10-minute window). Resolves to an error message or null.
  const editMessage = useCallback((messageId, body) => {
    if (!messageId || String(messageId).startsWith('c')) return Promise.resolve('Message is still sending');
    return api.put(`/api/chat/messages/${messageId}`, { body }).then(() => null).catch(e => e?.message || 'Could not edit message');
  }, []);

  // Delete my own message for everyone; the 'message:deleted' socket event updates every open screen.
  const deleteMessage = useCallback((messageId) => {
    if (!messageId || String(messageId).startsWith('c')) return Promise.resolve();
    return api.del(`/api/chat/messages/${messageId}`).catch(() => {});
  }, []);

  const toggleReaction = useCallback((messageId, emoji) => {
    if (!messageId || String(messageId).startsWith('c')) return; // still-optimistic (unsent) message, no real id yet
    api.post(`/api/chat/messages/${messageId}/react`, { emoji }).catch(() => {});
  }, []);

  // Pin/unpin a message — shared with the whole conversation. Updates the
  // flag on the message locally right away (the 'message:pin' socket
  // listener above will reconcile once the server round-trips it, same as
  // every other sender also sees); the full pinnedMessages list is reloaded
  // from the response so the pinned-messages bar never needs its own
  // optimistic row construction.
  const togglePinMessage = useCallback((messageId, pinned) => {
    if (!messageId || String(messageId).startsWith('c')) return;
    const apply = (list) => list.map(m => (m.id === messageId || String(m.id) === String(messageId)) ? { ...m, pinned: !pinned } : m);
    setMessages(apply);
    setGroupMessages(apply);
    setThreadMessages(apply);
    const req = pinned ? api.del(`/api/chat/messages/${messageId}/pin`) : api.post(`/api/chat/messages/${messageId}/pin`, {});
    req.then(() => {
      if (activeUserIdRef.current) {
        api.get(`/api/chat/messages/${activeUserIdRef.current}/pinned`).then(d => { if (d) setPinnedMessages(d.data || []); }).catch(() => {});
      } else if (activeGroupIdRef.current) {
        api.get(`/api/chat-groups/${activeGroupIdRef.current}/pinned-messages`).then(d => { if (d) setPinnedMessages(d.data || []); }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const pinConversation = useCallback((userId) => {
    return api.post(`/api/chat/pin/${userId}`, {}).then(r => {
      setConversations(prev => prev.map(c => c.user_id === userId ? { ...c, pinned: true } : c).sort((a, b) => (b.pinned - a.pinned) || 0));
      return r;
    });
  }, []);
  const unpinConversation = useCallback((userId) => {
    return api.del(`/api/chat/pin/${userId}`).then(r => {
      setConversations(prev => prev.map(c => c.user_id === userId ? { ...c, pinned: false } : c));
      return r;
    });
  }, []);
  const pinGroupConversation = useCallback((groupId) => {
    return api.post(`/api/chat-groups/${groupId}/pin`, {}).then(r => {
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, pinned: true } : g).sort((a, b) => (b.pinned - a.pinned) || 0));
      return r;
    });
  }, []);
  const unpinGroupConversation = useCallback((groupId) => {
    return api.del(`/api/chat-groups/${groupId}/pin`).then(r => {
      setGroups(prev => prev.map(g => g.id === groupId ? { ...g, pinned: false } : g));
      return r;
    });
  }, []);

  // Attachments go over REST, not the socket (multipart, not JSON) — the
  // server inserts the row and re-broadcasts it over the socket to both
  // ends, same as the text REST fallback. The optimistic entry carries a
  // local blob URL so the bubble (image/audio) renders instantly while the
  // upload is in flight, then gets replaced by the server row via clientId.
  const sendAttachment = useCallback((toUserId, file, kind, caption = '', replyTo = null) => {
    if (!file || !toUserId) return;
    const clientId = 'c' + Date.now() + Math.random().toString(36).slice(2);
    const previewUrl = URL.createObjectURL(file);
    const optimistic = {
      id: clientId, clientId, sender_id: myUserId, receiver_id: toUserId,
      body: caption.trim() || null, created_at: new Date().toISOString(), delivered_at: null, read_at: null,
      attachment_kind: kind, attachment_name: file.name, attachment_size: file.size,
      _optimistic: true, _uploading: true, _previewUrl: previewUrl,
      ...(replyTo ? {
        reply_to_message_id: replyTo.id,
        reply_to_body: replyTo.body,
        reply_to_attachment_kind: replyTo.attachment_kind,
        reply_to_attachment_name: replyTo.attachment_name,
        reply_to_sender_name: replyTo.sender_name,
      } : {}),
    };
    setMessages(prev => [...prev, optimistic]);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('toUserId', toUserId);
    fd.append('clientId', clientId);
    if (caption.trim()) fd.append('body', caption.trim());
    if (replyTo?.id) fd.append('replyToMessageId', replyTo.id);
    const onProgress = (pct) => setMessages(prev => prev.map(m => m.clientId === clientId ? { ...m, _uploadProgress: pct } : m));
    api.uploadWithProgress('/api/chat/messages/attachment', fd, onProgress)
      .then(row => {
        URL.revokeObjectURL(previewUrl);
        if (row) setMessages(prev => prev.map(m => m.clientId === clientId ? row : m));
      })
      .catch(() => {
        setMessages(prev => prev.map(m => m.clientId === clientId ? { ...m, _uploading: false, _failed: true } : m));
      });
  }, [myUserId]);

  // ── Groups/channels ──────────────────────────────────────────────────
  const createGroup = useCallback((name, memberUserIds) => {
    return api.post('/api/chat-groups', { name, kind: 'group', memberUserIds }).then(g => { loadGroups(); return g; });
  }, [loadGroups]);

  const createChannel = useCallback((name, description, memberUserIds, visibility = 'organization') => {
    return api.post('/api/chat-groups', { name, kind: 'channel', description, memberUserIds, visibility }).then(g => { loadGroups(); return g; });
  }, [loadGroups]);

  const joinChannel = useCallback((groupId) => {
    return api.post(`/api/chat-groups/${groupId}/join`, {}).then(g => { loadGroups(); loadChannels(); return g; });
  }, [loadGroups, loadChannels]);

  const inviteGuest = useCallback((groupId, email, name) => {
    return api.post(`/api/chat-groups/${groupId}/invite-guest`, { email, name });
  }, []);

  const setGroupIcon = useCallback((groupId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.upload(`/api/chat-groups/${groupId}/icon`, fd).then(r => { loadGroups(); return r; });
  }, [loadGroups]);

  // ── Owner-only group management (rename / delete / promote) ───────────
  const renameGroup = useCallback((groupId, name, description) => {
    return api.put(`/api/chat-groups/${groupId}`, { name, description }).then(g => {
      setGroups(prev => prev.map(x => x.id === groupId ? { ...x, ...g } : x));
      return g;
    });
  }, []);

  const deleteGroup = useCallback((groupId) => {
    return api.del(`/api/chat-groups/${groupId}`).then(r => {
      setGroups(prev => prev.filter(g => g.id !== groupId));
      if (activeGroupIdRef.current === groupId) {
        setActiveGroupId(null);
        setGroupMessages([]);
        setGroupMembers([]);
      }
      return r;
    });
  }, []);

  const promoteMember = useCallback((groupId, userId) => {
    return api.post(`/api/chat-groups/${groupId}/members/${userId}/promote`, {}).then(r => {
      setGroupMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: 'owner' } : m));
      return r;
    });
  }, []);

  const demoteMember = useCallback((groupId, userId) => {
    return api.post(`/api/chat-groups/${groupId}/members/${userId}/demote`, {}).then(r => {
      setGroupMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: 'member' } : m));
      return r;
    });
  }, []);

  const addMembers = useCallback((groupId, userIds) => {
    return api.post(`/api/chat-groups/${groupId}/members`, { userIds }).then(r => {
      api.get(`/api/chat-groups/${groupId}/members`).then(d => { if (d) setGroupMembers(d.data || []); }).catch(() => {});
      loadGroups();
      return r;
    });
  }, [loadGroups]);

  const removeMember = useCallback((groupId, userId) => {
    return api.del(`/api/chat-groups/${groupId}/members/${userId}`).then(r => {
      setGroupMembers(prev => prev.filter(m => m.user_id !== userId));
      loadGroups();
      return r;
    });
  }, [loadGroups]);

  const openGroup = useCallback((groupId) => {
    setActiveUserId(null);
    writeStoredActive(null);
    setMessages([]);
    setActiveGroupId(groupId);
    setGroupMessages([]);
    setGroupTypingUsers([]);
    setPinnedMessages([]);
    api.get(`/api/chat-groups/${groupId}/messages`).then(d => { if (d) setGroupMessages((d.data || []).slice().reverse()); }).catch(() => {});
    api.get(`/api/chat-groups/${groupId}/members`).then(d => { if (d) setGroupMembers(d.data || []); }).catch(() => {});
    api.get(`/api/chat-groups/${groupId}/pinned-messages`).then(d => { if (d) setPinnedMessages(d.data || []); }).catch(() => {});
    // GET /messages above already marks the group read server-side.
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, unread_count: 0 } : g));
  }, []);

  const closeGroup = useCallback(() => {
    setActiveGroupId(null);
    setGroupMessages([]);
    setGroupMembers([]);
    setPinnedMessages([]);
  }, []);

  const setGroupTyping = useCallback((groupId, isTyping) => {
    if (!groupId) return;
    socketRef.current?.emit(isTyping ? 'group:typing:start' : 'group:typing:stop', { groupId });
  }, []);

  const sendGroupMessage = useCallback((groupId, body, mentionedUserIds = [], replyTo = null) => {
    const text = body.trim();
    if (!text || !groupId) return;
    const clientId = 'c' + Date.now() + Math.random().toString(36).slice(2);
    const optimistic = {
      id: clientId, clientId, sender_id: myUserId, group_id: groupId, sender_name: 'You',
      body: text, created_at: new Date().toISOString(), delivered_at: null, read_at: null,
      mentioned_user_ids: mentionedUserIds, _optimistic: true,
      ...(replyTo ? {
        reply_to_message_id: replyTo.id,
        reply_to_body: replyTo.body,
        reply_to_attachment_kind: replyTo.attachment_kind,
        reply_to_attachment_name: replyTo.attachment_name,
        reply_to_sender_name: replyTo.sender_name,
      } : {}),
    };
    setGroupMessages(prev => [...prev, optimistic]);
    socketRef.current?.emit('group:message:send', { groupId, body: text, mentionedUserIds, clientId, replyToMessageId: replyTo?.id }, (res) => {
      if (res?.ok) setGroupMessages(prev => prev.map(m => m.clientId === clientId ? res.message : m));
    });
  }, [myUserId]);

  const sendGroupAttachment = useCallback((groupId, file, kind, caption = '', mentionedUserIds = [], replyTo = null) => {
    if (!file || !groupId) return;
    const clientId = 'c' + Date.now() + Math.random().toString(36).slice(2);
    const previewUrl = URL.createObjectURL(file);
    const optimistic = {
      id: clientId, clientId, sender_id: myUserId, group_id: groupId, sender_name: 'You',
      body: caption.trim() || null, created_at: new Date().toISOString(), delivered_at: null, read_at: null,
      attachment_kind: kind, attachment_name: file.name, attachment_size: file.size,
      mentioned_user_ids: mentionedUserIds, _optimistic: true, _uploading: true, _previewUrl: previewUrl,
      ...(replyTo ? {
        reply_to_message_id: replyTo.id,
        reply_to_body: replyTo.body,
        reply_to_attachment_kind: replyTo.attachment_kind,
        reply_to_attachment_name: replyTo.attachment_name,
        reply_to_sender_name: replyTo.sender_name,
      } : {}),
    };
    setGroupMessages(prev => [...prev, optimistic]);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('clientId', clientId);
    if (caption.trim()) fd.append('body', caption.trim());
    if (mentionedUserIds.length) fd.append('mentionedUserIds', JSON.stringify(mentionedUserIds));
    if (replyTo?.id) fd.append('replyToMessageId', replyTo.id);
    const onProgress = (pct) => setGroupMessages(prev => prev.map(m => m.clientId === clientId ? { ...m, _uploadProgress: pct } : m));
    api.uploadWithProgress(`/api/chat-groups/${groupId}/messages/attachment`, fd, onProgress)
      .then(row => {
        URL.revokeObjectURL(previewUrl);
        if (row) setGroupMessages(prev => prev.map(m => m.clientId === clientId ? row : m));
      })
      .catch(() => {
        setGroupMessages(prev => prev.map(m => m.clientId === clientId ? { ...m, _uploading: false, _failed: true } : m));
      });
  }, [myUserId]);

  // ── Threads ──────────────────────────────────────────────────────────
  const openThreadView = useCallback((rootId) => {
    setActiveThreadRootId(rootId);
    setThreadMessages([]);
    api.get(`/api/threads/${rootId}`).then(d => { if (d) setThreadMessages(d.data || []); }).catch(() => {});
    loadThreads(); // this open marks it read server-side; refresh the unread state
  }, [loadThreads]);

  const closeThreadView = useCallback(() => {
    setActiveThreadRootId(null);
    setThreadMessages([]);
  }, []);

  const sendThreadReply = useCallback((body, mentionedUserIds = []) => {
    const rootId = activeThreadRootIdRef.current;
    const text = body.trim();
    if (!text || !rootId) return;
    const clientId = 'c' + Date.now() + Math.random().toString(36).slice(2);
    const optimistic = {
      id: clientId, clientId, sender_id: myUserId, parent_message_id: rootId, sender_name: 'You',
      body: text, created_at: new Date().toISOString(), mentioned_user_ids: mentionedUserIds, _optimistic: true,
    };
    setThreadMessages(prev => [...prev, optimistic]);
    socketRef.current?.emit('thread:reply:send', { rootId, body: text, mentionedUserIds, clientId }, (res) => {
      if (res?.ok) setThreadMessages(prev => prev.map(m => m.clientId === clientId ? res.message : m));
    });
  }, [myUserId]);

  const sendThreadReplyAttachment = useCallback((file, kind, caption = '', mentionedUserIds = []) => {
    const rootId = activeThreadRootIdRef.current;
    if (!file || !rootId) return;
    const clientId = 'c' + Date.now() + Math.random().toString(36).slice(2);
    const previewUrl = URL.createObjectURL(file);
    const optimistic = {
      id: clientId, clientId, sender_id: myUserId, parent_message_id: rootId, sender_name: 'You',
      body: caption.trim() || null, created_at: new Date().toISOString(),
      attachment_kind: kind, attachment_name: file.name, attachment_size: file.size,
      mentioned_user_ids: mentionedUserIds, _optimistic: true, _uploading: true, _previewUrl: previewUrl,
    };
    setThreadMessages(prev => [...prev, optimistic]);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('clientId', clientId);
    if (caption.trim()) fd.append('body', caption.trim());
    if (mentionedUserIds.length) fd.append('mentionedUserIds', JSON.stringify(mentionedUserIds));
    const onProgress = (pct) => setThreadMessages(prev => prev.map(m => m.clientId === clientId ? { ...m, _uploadProgress: pct } : m));
    api.uploadWithProgress(`/api/threads/${rootId}/attachment`, fd, onProgress)
      .then(row => {
        URL.revokeObjectURL(previewUrl);
        if (row) setThreadMessages(prev => prev.map(m => m.clientId === clientId ? row : m));
      })
      .catch(() => {
        setThreadMessages(prev => prev.map(m => m.clientId === clientId ? { ...m, _uploading: false, _failed: true } : m));
      });
  }, [myUserId]);

  // ── Calls ────────────────────────────────────────────────────────────
  // target: { type: 'dm', toUserId } | { type: 'group', groupId }.
  // Tries to create a real, server-generated Meet link via Google Calendar —
  // the caller's own connection if they have one, otherwise the
  // organization's default connection (one admin connects once), otherwise
  // — for a DM call — the person being called, if THEY'VE connected their
  // own (see server/services/googleCalendar.js#callCalendarCandidates).
  // So calls get a real auto-join link as long as anyone involved has
  // something connected, without every single employee needing to connect
  // their own first. Falls back to the manual meet.google.com/new flow
  // only if nobody involved has anything connected. The link is also
  // posted as a normal chat message (so it's in history even for someone
  // who missed the ring) and a live 'call:start' signal is sent so the
  // other side's chat widget rings immediately.
  const startCall = useCallback(async (target) => {
    let meetLink = null;
    try {
      // An ad-hoc Meet space, not a calendar event — a quick call
      // shouldn't leave a lasting entry on anyone's calendar; only an
      // explicit Schedule Meeting should (see ScheduleMeetingPanel).
      const res = await api.post('/api/google-calendar/create-call-meeting', {
        toUserId: target.type === 'dm' ? target.toUserId : undefined,
      });
      meetLink = res?.hangoutLink || null;
    } catch (e) { /* no calendar connection anywhere in the org yet — fall back below */ }

    window.open(meetLink || 'https://meet.google.com/new', '_blank', 'noopener,noreferrer');
    const text = meetLink
      ? `🎥 Started a video call — ${meetLink}`
      : '🎥 Started a video call — paste the Meet link here once it opens in the new tab.';
    if (target.type === 'dm') sendMessage(target.toUserId, text);
    else sendGroupMessage(target.groupId, text);

    socketRef.current?.emit('call:start', {
      toUserId: target.type === 'dm' ? target.toUserId : undefined,
      groupId: target.type === 'group' ? target.groupId : undefined,
      meetLink,
    });

    // The caller previously got zero feedback beyond the new tab and the
    // chat message — this "Calling…" banner (with its own ring-back tone)
    // mirrors the recipient's incoming-call banner so both sides see
    // something happened, and makes the no-Calendar-connected case explicit
    // instead of silently repeating the same "paste the link" text forever.
    setOutgoingCall({
      meetLink, targetName: target.name || 'them',
      toUserId: target.type === 'dm' ? target.toUserId : undefined,
      groupId: target.type === 'group' ? target.groupId : undefined,
    });
    clearTimeout(outgoingCallTimeoutRef.current);
    startRingtone();
    outgoingCallTimeoutRef.current = setTimeout(() => { stopRingtone(); setOutgoingCall(null); }, 20000);
  }, [sendMessage, sendGroupMessage]);

  const cancelOutgoingCall = useCallback(() => {
    const cur = outgoingCallRef.current;
    socketRef.current?.emit('call:cancel', { toUserId: cur?.toUserId, groupId: cur?.groupId });
    clearTimeout(outgoingCallTimeoutRef.current);
    stopRingtone();
    setOutgoingCall(null);
  }, []);

  const muteOutgoingCall = useCallback(() => stopRingtone(), []);

  const answerCall = useCallback(() => {
    // The caller only ever shares a real link if their own Google Calendar
    // is connected (see startCall) — otherwise there's nothing to open yet,
    // and silently doing nothing on click reads as a broken button. Surface
    // that explicitly instead.
    if (incomingCall?.meetLink) {
      window.open(incomingCall.meetLink, '_blank', 'noopener,noreferrer');
    } else {
      window.alert("The caller hasn't shared a Meet link yet — check the chat for it once they paste it in, or ask them to connect Google Calendar so this joins automatically next time.");
      return; // leave the banner up — they may still get the link shortly
    }
    socketRef.current?.emit('call:answer', { toUserId: incomingCall.fromUserId, groupId: incomingCall.groupId });
    clearTimeout(incomingCallTimeoutRef.current);
    stopRingtone();
    setIncomingCall(null);
  }, [incomingCall]);

  const declineCall = useCallback(() => {
    const cur = incomingCallRef.current;
    socketRef.current?.emit('call:decline', { toUserId: cur?.fromUserId, groupId: cur?.groupId });
    clearTimeout(incomingCallTimeoutRef.current);
    stopRingtone();
    setIncomingCall(null);
  }, []);

  // Silences the ring without dismissing the banner — Join/Decline stay
  // available until the 20s auto-timeout (or either button) clears it.
  const muteIncomingCall = useCallback(() => {
    const cur = incomingCallRef.current;
    stopRingtone();
    socketRef.current?.emit('call:mute', { toUserId: cur?.fromUserId, groupId: cur?.groupId });
  }, []);

  return {
    people, conversations, unreadTotal,
    activeUserId, messages, typingFrom,
    openThread, closeThread, sendMessage, sendAttachment, setTyping, toggleReaction, deleteMessage, editMessage, setPanelOpen,
    pinConversation, unpinConversation,

    groups, channels, activeGroupId, groupMessages, groupMembers, groupTypingUsers,
    loadChannels, createGroup, createChannel, joinChannel, inviteGuest, setGroupIcon,
    openGroup, closeGroup, setGroupTyping, sendGroupMessage, sendGroupAttachment,
    renameGroup, deleteGroup, promoteMember, demoteMember, removeMember, addMembers,
    pinGroupConversation, unpinGroupConversation,

    pinnedMessages, togglePinMessage,

    incomingCall, startCall, answerCall, declineCall, muteIncomingCall,
    outgoingCall, cancelOutgoingCall, muteOutgoingCall,

    threads, activeThreadRootId, threadMessages, loadThreads,
    openThreadView, closeThreadView, sendThreadReply, sendThreadReplyAttachment,
  };
}
